/**
 * cart-cross-sell.js
 *
 * Powers the "You may like" style carousel in the cart drawer.
 *
 * Behavior:
 * - A fallback collection (chosen by the merchant, e.g. a "Best sellers"
 *   collection) is rendered server-side and shown immediately.
 * - Shopify's native <recently-viewed> element (already used elsewhere in
 *   this theme) fetches, client-side, the products the visitor actually
 *   looked at before adding to cart.
 * - As soon as that recently-viewed list is ready, if it contains at least
 *   one product, it replaces the fallback content. If the visitor has no
 *   recently viewed products, the fallback stays as-is.
 * - If there is nothing to show at all (no fallback collection configured
 *   and no recently viewed products), the whole widget hides itself.
 * - Products already in the cart are never suggested. The fallback list is
 *   filtered server-side (Liquid) using the cart handles at render time,
 *   AND filtered again here client-side against a fresh /cart.js fetch (so
 *   it stays correct even if the drawer's HTML was rendered before the most
 *   recent add-to-cart). The recently-viewed list, fetched entirely
 *   client-side, is filtered the same way.
 *
 * Navigation:
 * - This carousel is deliberately NOT built on the theme's native
 *   grid-slider component. That component only attaches prev/next click
 *   handlers when it feels like it (it skips them on devices it thinks are
 *   touch/mobile), and even when it does, native swipe/drag/scroll stays
 *   active on top of it regardless.
 * - Here the track sits inside a plain `overflow: hidden` viewport with no
 *   scroll mechanism of its own (see the CSS in cart-drawer.liquid) — there
 *   is nothing to swipe, drag, or scroll. The only way the track ever moves
 *   is a JS-driven `transform: translateX()` triggered by the two arrow
 *   buttons, wired up below. This is identical on mobile and desktop.
 */
(function () {
  'use strict';

  if (customElements.get('cart-cross-sell')) return;

  const ITEM_SELECTOR = '.product-item';
  const WIDGET_SELECTOR = '[data-cross-sell-carousel]';

  function getHandleFromUrl(url) {
    if (!url) return '';

    const match = url.split('?')[0].match(/\/products\/([^/]+)/);

    return match ? match[1] : '';
  }

  function removeItemsInCart(items, excludedHandles) {
    if (!excludedHandles.length) return items;

    return items.filter((item) => {
      const handle = getHandleFromUrl(item.getAttribute('data-url'));

      return !excludedHandles.includes(handle);
    });
  }

  customElements.define(
    'cart-cross-sell',
    class CartCrossSell extends HTMLElement {
      constructor() {
        super();
        this.target = this.querySelector('[data-cross-sell-target]');
        this.viewport = this.querySelector('[data-cross-sell-viewport]');
        this.recentlyViewed = this.querySelector('recently-viewed');
        this.prevButton = this.querySelector('[data-button-prev]');
        this.nextButton = this.querySelector('[data-button-next]');
        this.offset = 0;
      }

      connectedCallback() {
        this.excludedHandles = (this.dataset.cartHandles || '')
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean);

        // The server-rendered fallback list was filtered against the cart at
        // render time, but re-filter it here too in case the cart changed
        // since (e.g. the drawer HTML was cached, or a quick-add happened
        // without a full section re-render).
        this.filterTarget();

        this.refreshExcludedHandlesFromLiveCart().then(() => this.filterTarget());

        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('theme:recently-viewed:loaded', () => {
            this.handleRecentlyViewedLoaded();
          });
        }

        if (this.prevButton) {
          this.prevButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.slide(-1);
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.slide(1);
          });
        }

        this._onResize = () => this.resetSlider();
        window.addEventListener('resize', this._onResize);

        // The drawer (and this carousel inside it) can be laid out while
        // still hidden (display:none / width:0), which would make every
        // width measurement below come back as 0. A ResizeObserver on the
        // viewport catches the moment the drawer actually opens and the
        // real width becomes available, so the arrows end up in the right
        // enabled/disabled state without needing to know anything about how
        // this specific theme opens its drawer.
        if (this.viewport && 'ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(() => this.updateArrows());
          this._resizeObserver.observe(this.viewport);
        }

        this.resetSlider();
      }

      disconnectedCallback() {
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._resizeObserver) this._resizeObserver.disconnect();
      }

      refreshExcludedHandlesFromLiveCart() {
        return fetch('/cart.js')
          .then((response) => response.json())
          .then((cart) => {
            const liveHandles = (cart.items || []).map((item) => item.handle).filter(Boolean);

            liveHandles.forEach((handle) => {
              if (!this.excludedHandles.includes(handle)) {
                this.excludedHandles.push(handle);
              }
            });
          })
          .catch(() => {});
      }

      filterTarget() {
        if (!this.target || !this.excludedHandles || !this.excludedHandles.length) return;

        const items = Array.from(this.target.querySelectorAll(ITEM_SELECTOR));
        const toRemove = items.filter((item) => {
          const handle = getHandleFromUrl(item.getAttribute('data-url'));

          return this.excludedHandles.includes(handle);
        });

        if (toRemove.length) {
          toRemove.forEach((item) => item.remove());
          this.updateVisibility();
          this.resetSlider();
        }
      }

      handleRecentlyViewedLoaded() {
        if (!this.target || !this.recentlyViewed) return;

        let items = Array.from(this.recentlyViewed.querySelectorAll(ITEM_SELECTOR));

        items = removeItemsInCart(items, this.excludedHandles || []);

        if (items.length) {
          this.target.innerHTML = '';

          items.forEach((item) => {
            this.target.appendChild(item.cloneNode(true));
          });
        }

        this.updateVisibility();
        this.resetSlider();
      }

      updateVisibility() {
        const hasProducts = this.target && this.target.querySelectorAll(ITEM_SELECTOR).length > 0;
        const widget = this.closest(WIDGET_SELECTOR) || this;

        widget.classList.toggle('hidden', !hasProducts);
      }

      getItems() {
        return this.target ? Array.from(this.target.querySelectorAll('[data-grid-item]')) : [];
      }

      // Distance (in px) between the start of one card and the start of the
      // next, gaps included — i.e. how far to move per arrow click.
      getStep() {
        const items = this.getItems();

        if (!items.length) return 0;
        if (items.length === 1) return items[0].getBoundingClientRect().width;

        return items[1].offsetLeft - items[0].offsetLeft;
      }

      getMaxOffset() {
        if (!this.target || !this.viewport) return 0;

        return Math.max(0, this.target.scrollWidth - this.viewport.clientWidth);
      }

      resetSlider() {
        this.offset = 0;

        if (this.target) {
          this.target.style.transform = 'translateX(0px)';
        }

        this.updateArrows();
      }

      slide(direction) {
        const step = this.getStep();

        if (!step || !this.target) return;

        const maxOffset = this.getMaxOffset();
        let next = this.offset + direction * step;

        if (next < 0) next = 0;
        if (next > maxOffset) next = maxOffset;

        this.offset = next;
        this.target.style.transform = 'translateX(-' + this.offset + 'px)';
        this.updateArrows();
      }

      updateArrows() {
        const maxOffset = this.getMaxOffset();

        if (this.prevButton) {
          this.prevButton.disabled = this.offset <= 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled = this.offset >= maxOffset - 1;
        }
      }
    }
  );
})();